import { useEffect, useRef } from "react";
import { useSocket } from "src/context/SocketContext";
import { fetchBuyingStatus, fetchPortingStatus } from "../../PaySetupFee/flow-mobile-stripe.actions";

export interface FlowMobileSetupSocketPayload {
  companyId: number;
  subscriptionId: string;
  invoiceUid?: string | null;
  kind: "PORTING" | "BUYING";
  provisioned: boolean;
  phoneNumber?: string | null;
}

interface UseFlowMobileSetupSyncParams {
  companyId?: number;
  subscriptionId?: string;
  onProvisioned: (payload: unknown) => void;
  onPending: (message: string) => void;
  onError: (message: string) => void;
}

const roomForCompany = (companyId: number) => `room_flow_mobile_${companyId}`;

const pendingMessage = (kind: "porting" | "buying") =>
  kind === "porting"
    ? "Waiting for the porting payment to be confirmed. This request stays blocked until then."
    : "Waiting for the Flow Mobile subscription payment to be confirmed.";

export function useFlowMobileSetupSync({ companyId, subscriptionId, onProvisioned, onPending, onError }: UseFlowMobileSetupSyncParams) {
  const { socket } = useSocket() || {};
  const onProvisionedRef = useRef(onProvisioned);
  const onPendingRef = useRef(onPending);
  const onErrorRef = useRef(onError);
  onProvisionedRef.current = onProvisioned;
  onPendingRef.current = onPending;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!companyId || !socket) return;

    const room = roomForCompany(companyId);
    const join = () => {
      socket.emit("join", [room]);
    };

    const onStatus = (raw: unknown) => {
      const payload: FlowMobileSetupSocketPayload = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!payload?.provisioned) return;
      if (subscriptionId && String(payload.subscriptionId) !== String(subscriptionId)) return;
      sessionStorage.removeItem("flowMobilePortingInvoice");
      sessionStorage.removeItem("flowMobileBuyingSubscription");
      onProvisionedRef.current(payload);
    };

    if (socket.connected) join();
    socket.on("connect", join);
    socket.on("flowMobile:setupStatus", onStatus);

    return () => {
      socket.off("connect", join);
      socket.off("flowMobile:setupStatus", onStatus);
      socket.emit("leaveFlowMobileRoom", companyId);
    };
  }, [companyId, socket, subscriptionId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("flowMobilePorting") === "cancelled") {
      sessionStorage.removeItem("flowMobilePortingInvoice");
      onErrorRef.current("The porting payment was cancelled. The request stays blocked until the payment is confirmed.");
      return;
    }

    const invoiceUid = params.get("flowMobilePortingInvoice") || sessionStorage.getItem("flowMobilePortingInvoice");
    const buyingSubscription = sessionStorage.getItem("flowMobileBuyingSubscription");
    if (!invoiceUid && !buyingSubscription) return;

    let cancelled = false;
    const catchUp = async () => {
      try {
        if (invoiceUid) {
          const status = await fetchPortingStatus(invoiceUid);
          if (cancelled) return;
          if (status.status === "paid" && status.provisioned) {
            sessionStorage.removeItem("flowMobilePortingInvoice");
            onProvisionedRef.current(status);
            return;
          }
          if (status.status === "paid") {
            onPendingRef.current(pendingMessage("porting"));
            return;
          }
          sessionStorage.removeItem("flowMobilePortingInvoice");
          return;
        }

        if (buyingSubscription) {
          const status = await fetchBuyingStatus(buyingSubscription);
          if (cancelled) return;
          if (status.provisioned || status.status === "provisioned") {
            sessionStorage.removeItem("flowMobileBuyingSubscription");
            onProvisionedRef.current(status);
            return;
          }
          onPendingRef.current(pendingMessage("buying"));
        }
      } catch (error) {
        console.error("Flow Mobile setup status check failed:", error);
      }
    };

    void catchUp();
    return () => {
      cancelled = true;
    };
  }, []);
}
